import React, { useEffect, useId, useRef, useState } from 'react';
import { apiUrl } from '../config/api';
import { isValidCityName, normalizeLocationName, sanitizeCityInput } from '../utils/locationFormatting';
import './LocationFields.css';

const optionCatalogs = new Map();
const pendingOptionCatalogs = new Map();
const citySearchCache = new Map();
const CITY_SEARCH_MIN_LENGTH = 3;
const CITY_SEARCH_DEBOUNCE_MS = 500;
const CITY_SEARCH_CACHE_TTL_MS = 30 * 60 * 1000;
const MAX_CITY_SEARCH_CACHE_ENTRIES = 100;

const searchable = (value) => String(value || '')
  .normalize('NFD')
  .replace(/[\u0300-\u036f]/g, '')
  .toLocaleLowerCase()
  .trim();

const catalogKey = (kind, country = '') => `${kind}:${searchable(country)}`;

// Countries and administrative regions are static reference data. Fetch the
// complete catalogue once per scope and filter it in the browser afterwards.
const loadOptionCatalog = async (kind, country = '') => {
  const key = catalogKey(kind, country);
  if (optionCatalogs.has(key)) return optionCatalogs.get(key);
  if (pendingOptionCatalogs.has(key)) return pendingOptionCatalogs.get(key);

  const request = (async () => {
    const params = new URLSearchParams({ kind });
    if (country) params.set('country', country);
    const response = await fetch(apiUrl(`/api/locations/options?${params}`));
    const result = await response.json();
    if (!response.ok) throw new Error(result.error || 'Location lookup failed');
    const locations = Array.isArray(result.locations) ? result.locations : [];
    optionCatalogs.set(key, locations);
    return locations;
  })();

  pendingOptionCatalogs.set(key, request);
  try {
    return await request;
  } finally {
    pendingOptionCatalogs.delete(key);
  }
};

const filterOptionCatalog = (options, kind, query) => {
  const needle = searchable(query);
  if (!needle) return options;
  return options.filter((option) => searchable(
    kind === 'country'
      ? `${option.country} ${option.countryCode || ''}`
      : `${option.region} ${option.regionCode || ''}`
  ).includes(needle));
};

const verifiedValue = (kind, location) => (
  kind === 'country' ? location.country : location.region
);

const LocationCombobox = ({ kind, label, value, country, required, disabled, onSelect, placeholder }) => {
  const uid = useId();
  const rootRef = useRef(null);
  const [query, setQuery] = useState(value || '');
  const [catalog, setCatalog] = useState([]);
  const [options, setOptions] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [lookupError, setLookupError] = useState('');

  // Do not overwrite an in-progress edit when clearing the previously committed
  // value in the parent. This was why editing a saved location erased the first
  // character typed and made the field appear locked.
  useEffect(() => {
    if (!open) setQuery(value || '');
  }, [open, value]);
  useEffect(() => {
    const close = (event) => {
      if (!rootRef.current?.contains(event.target)) {
        setOpen(false);
        setQuery(value || '');
      }
    };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, [value]);

  useEffect(() => {
    if (!open || (kind === 'state' && !country)) {
      setCatalog([]);
      setOptions([]);
      setLoading(false);
      setLookupError('');
      return undefined;
    }
    let active = true;
    setCatalog([]);
    setLoading(true);
    setLookupError('');
    loadOptionCatalog(kind, country).then((locations) => {
      if (!active) return;
      setCatalog(locations);
    }).catch(() => {
      if (!active) return;
      setCatalog([]);
      setLookupError('Locations could not be loaded. Try again.');
    }).finally(() => {
      if (active) setLoading(false);
    });
    return () => { active = false; };
  }, [country, kind, open]);

  useEffect(() => {
    setOptions(filterOptionCatalog(catalog, kind, query));
  }, [catalog, kind, query]);

  const choose = (location) => {
    const selected = normalizeLocationName(verifiedValue(kind, location));
    setQuery(selected);
    setOpen(false);
    setOptions([]);
    onSelect(selected, location);
  };

  return <label className="location-fields__field" ref={rootRef}>
    <span>{label}{required ? ' *' : ''}</span>
    <div className="location-fields__combobox">
      <input
        id={`${uid}-${kind}`}
        role="combobox"
        aria-expanded={open}
        aria-controls={`${uid}-${kind}-options`}
        aria-autocomplete="list"
        autoComplete="off"
        disabled={disabled}
        required={required}
        value={query}
        placeholder={placeholder}
        onFocus={() => setOpen(true)}
        onChange={(event) => {
          setQuery(event.target.value);
          setOpen(true);
          if (value) onSelect('', null);
        }}
      />
      {loading ? <span className="location-fields__spinner" role="status" aria-label="Loading locations" /> : <span className="location-fields__toggle" aria-hidden="true" />}
      {open && <div id={`${uid}-${kind}-options`} className="location-fields__options" role="listbox">
        {options.map((option) => <button type="button" role="option" aria-selected={value === verifiedValue(kind, option)} key={option.id} onMouseDown={(event) => event.preventDefault()} onClick={() => choose(option)}>
          <span><strong>{kind === 'country' ? option.country : option.region}</strong><small>{kind === 'state' ? option.country : 'Verified country'}</small></span>{value === verifiedValue(kind, option) && <span className="location-fields__selected">Selected</span>}
        </button>)}
        {!loading && lookupError && <p role="alert">{lookupError}</p>}
        {!loading && !lookupError && options.length === 0 && <p>No verified locations found. Try another spelling.</p>}
      </div>}
    </div>
    <small>{value ? 'Verified location selected' : kind === 'state' && !country ? 'Select a country first' : 'Choose a result from the dropdown'}</small>
  </label>;
};

const LocationFields = ({ value, onChange, required = false, disabled = false, labels = {}, className = '' }) => {
  const location = { country: '', state: '', city: '', ...value };
  const [cityOptions, setCityOptions] = useState([]);
  const [cityOpen, setCityOpen] = useState(false);
  const [cityLoading, setCityLoading] = useState(false);
  const [cityLookupError, setCityLookupError] = useState('');
  const cityId = useId();

  useEffect(() => {
    const query = location.city.trim();
    if (!cityOpen || query.length < CITY_SEARCH_MIN_LENGTH || !location.country) {
      setCityOptions([]);
      setCityLoading(false);
      setCityLookupError('');
      return undefined;
    }
    const controller = new AbortController();
    setCityLoading(true);
    setCityLookupError('');
    const timer = setTimeout(async () => {
      try {
        const params = new URLSearchParams({ q: query, kind: 'city', country: location.country });
        if (location.state) params.set('state', location.state);
        const cacheKey = searchable(params.toString());
        const cached = citySearchCache.get(cacheKey);
        if (cached && cached.expiresAt > Date.now()) {
          setCityOptions(cached.locations);
          return;
        }
        if (cached) citySearchCache.delete(cacheKey);
        const response = await fetch(apiUrl(`/api/locations/search?${params}`), { signal: controller.signal });
        const result = await response.json();
        if (!response.ok) throw new Error(result.error || 'Location lookup failed');
        const locations = Array.isArray(result.locations) ? result.locations : [];
        if (citySearchCache.size >= MAX_CITY_SEARCH_CACHE_ENTRIES) {
          citySearchCache.delete(citySearchCache.keys().next().value);
        }
        citySearchCache.set(cacheKey, { locations, expiresAt: Date.now() + CITY_SEARCH_CACHE_TTL_MS });
        setCityOptions(locations);
      } catch (error) {
        if (error.name !== 'AbortError') {
          setCityOptions([]);
          setCityLookupError('Suggestions are unavailable, but you can enter the city manually.');
        }
      } finally {
        if (!controller.signal.aborted) setCityLoading(false);
      }
    }, CITY_SEARCH_DEBOUNCE_MS);
    return () => { clearTimeout(timer); controller.abort(); };
  }, [cityOpen, location.city, location.country, location.state]);

  const setCountry = (country, match) => onChange({
    ...location, country, state: '', city: '',
    latitude: match?.latitude ?? null, longitude: match?.longitude ?? null,
  });
  const setState = (state, match) => onChange({
    ...location, state, city: '',
    latitude: match?.latitude ?? null, longitude: match?.longitude ?? null,
  });
  const setCity = (city, match = null) => onChange({
    ...location,
    country: match?.country ? normalizeLocationName(match.country) : location.country,
    state: match?.region ? normalizeLocationName(match.region) : location.state,
    city: sanitizeCityInput(city),
    latitude: match?.latitude ?? null,
    longitude: match?.longitude ?? null,
  });

  return <div className={`location-fields ${className}`.trim()}>
    <LocationCombobox kind="country" label={labels.country || 'Country'} value={location.country} required={required} disabled={disabled} onSelect={setCountry} placeholder="Search for a country" />
    <LocationCombobox kind="state" label={labels.state || 'State / Province'} value={location.state} country={location.country} required={required} disabled={disabled || !location.country} onSelect={setState} placeholder="Search for a state or province" />
    <label className="location-fields__field location-fields__city">
      <span>{labels.city || 'City'}{required ? ' *' : ''}</span>
      <div className="location-fields__combobox">
        <input
          id={`${cityId}-city`}
          value={location.city}
          disabled={disabled || !location.country || !location.state}
          required={required}
          maxLength={100}
          autoComplete="address-level2"
          placeholder="Enter a city"
          aria-invalid={Boolean(location.city) && !isValidCityName(location.city)}
          onFocus={() => setCityOpen(true)}
          onBlur={() => {
            const normalized = normalizeLocationName(location.city);
            if (normalized !== location.city) setCity(normalized);
            setTimeout(() => setCityOpen(false), 150);
          }}
          onChange={(event) => { setCityOpen(true); setCity(event.target.value); }}
        />
        {cityLoading ? <span className="location-fields__spinner" role="status" aria-label="Loading cities" /> : <span className="location-fields__toggle" aria-hidden="true" />}
        {cityOpen && location.city.trim().length >= CITY_SEARCH_MIN_LENGTH && <div className="location-fields__options" role="listbox">
          {cityOptions.map((option) => <button type="button" role="option" aria-selected={location.city === normalizeLocationName(option.city)} key={option.id} onMouseDown={(event) => event.preventDefault()} onClick={() => { setCity(option.city, option); setCityOpen(false); }}>
            <span><strong>{normalizeLocationName(option.city)}</strong><small>{[normalizeLocationName(option.region), normalizeLocationName(option.country)].filter(Boolean).join(', ')}</small></span>
          </button>)}
          {!cityLoading && cityLookupError && <p role="alert">{cityLookupError}</p>}
          {!cityLoading && !cityLookupError && cityOptions.length === 0 && <p>No suggestions found. You can keep the city as entered.</p>}
        </div>}
      </div>
      <small>{location.city && isValidCityName(location.city) ? 'City entered' : `Type at least ${CITY_SEARCH_MIN_LENGTH} characters or choose a suggestion`}</small>
    </label>
  </div>;
};

export default LocationFields;
